import { Heading } from '@apollo/orbit';
import classnames from 'classnames';
import { GraphQLSchema } from 'graphql';
import _ from 'lodash';
import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { getSubjectSlugByGraphQLType } from 'src/app/graph/documentationPage/schemaPage/getSubjectSlugByGraphQLType';
import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { splitPrintedTypeString } from 'src/components/common/formattedTypeString/FormattedTypeString';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { LocalTimestamp } from 'src/components/timestamp/Timestamp';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import {
  changelogSingleChangeRouteConfig,
  variantRouteConfig,
} from '../../routes';
import { ChangeHighlighter } from '../changeHighlighter/ChangeHighlighter';
import { DetailedChangeIcon } from '../changeIcon/ChangeIcon';
import { ChangesCounters } from '../changesCounters/ChangesCounters';
import { getTypeOfChangeByChangeCodeAndTypeOfThing } from '../getTypeOfChangeByChangeCodeAndTypeOfThing';

export const ChangeLogEntry = ({
  historyItem,
  graphqlSchema,
}: {
  graphqlSchema: GraphQLSchema;
  historyItem: GraphQLTypes.SchemaTagDiffFragment;
}) => {
  const location = useLocation();
  const typeMap = graphqlSchema.getTypeMap();
  const dateString = new Date(historyItem.publishedAt).toLocaleDateString(
    undefined,
    {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    },
  );
  const { locationToReference } = useInternalGraphLinking();

  return (
    <div data-changelog-history-items-in-date className="flex w-full">
      <div className="hidden w-56 shrink-0 lg:block">
        <Heading
          as={NavLink}
          size="2xl"
          aria-level={2}
          to={changelogSingleChangeRouteConfig.locationFrom({
            location,
            fromRouteConfig: variantRouteConfig,
            patch: {
              version: historyItem.id,
            },
          })}
        >
          {dateString}
        </Heading>
      </div>
      <div className="grow">
        <NavLink
          to={changelogSingleChangeRouteConfig.locationFrom({
            location,
            fromRouteConfig: variantRouteConfig,
            patch: {
              version: historyItem.id,
            },
          })}
          className="flex items-baseline border-b border-b-primary pb-3 text-base text-secondary hover:text-link"
        >
          <span className="lg:hidden">
            {dateString}
            <span className="ml-1">at</span>
          </span>
          <LocalTimestamp
            className="ml-1 lowercase"
            value={historyItem.publishedAt}
            format="time-only"
          />
          <span className="mx-2">&bull;</span>
          <span className="font-mono">{historyItem.id.substr(0, 6)}</span>
          <div className="ml-auto flex">
            {historyItem.diffToPrevious?.changes && (
              <ChangesCounters changes={historyItem.diffToPrevious?.changes} />
            )}
          </div>
        </NavLink>
        {!historyItem.diffToPrevious?.changes ||
          (historyItem.diffToPrevious.changes.length === 0 && (
            <div className="mt-8">
              <NotFound
                title="No Schema Changes Detected"
                subtitle="No types or fields were modified in your composed schema."
              />
            </div>
          ))}
        <ul
          data-changelog-type-changes-in-history-item
          className="leading-loose"
        >
          {Object.entries(
            _.groupBy(
              historyItem.diffToPrevious?.changes,
              (change) => change.parentNode?.name,
            ),
          ).map(([typeName, changesOnType]) => {
            const changesRelevantToTypeAndNotFields = changesOnType.filter(
              (change) =>
                getTypeOfChangeByChangeCodeAndTypeOfThing(
                  change.code,
                  'type',
                ) !== 'SOMETHING_INSIDE_THIS_CHANGED',
            );
            const changesInFields = _.difference(
              changesOnType,
              changesRelevantToTypeAndNotFields,
            );
            const typeOfChangeForType = changesRelevantToTypeAndNotFields[0]
              ? getTypeOfChangeByChangeCodeAndTypeOfThing(
                  changesRelevantToTypeAndNotFields[0].code,
                  'type',
                )
              : null;

            const type: (typeof typeMap)[string] | undefined | null =
              typeMap[typeName];

            return (
              <li key={typeName} className="mt-3">
                <h5 className="flex items-center gap-1">
                  <DetailedChangeIcon
                    changeType={typeOfChangeForType}
                    className="mr-1"
                  />
                  <ChangeHighlighter typeOfChange={typeOfChangeForType}>
                    {type ? (
                      <NavLink
                        className="hover:underline"
                        title="See in Schema Reference"
                        to={locationToReference({
                          category: getSubjectSlugByGraphQLType(type),
                          graphqlTypeName: typeName,
                        })}
                      >
                        {typeName}
                      </NavLink>
                    ) : (
                      typeName
                    )}
                  </ChangeHighlighter>{' '}
                  <span className="text-sm lowercase text-secondary">
                    {changesRelevantToTypeAndNotFields.length
                      ? _.uniqBy(
                          changesRelevantToTypeAndNotFields,
                          (change) => change.code,
                        ).map((change) => (
                          <span key={change.code} className="mx-1">
                            {change.code.replace(/_/g, ' ')}
                          </span>
                        ))
                      : 'type modified'}
                  </span>
                </h5>
                <ul data-changelog-changes-in-type className="ml-5 mt-3">
                  {Object.entries(
                    _.groupBy(
                      changesInFields,
                      (change) => change.childNode?.name,
                    ),
                  ).map(([fieldName, changesOnField]) => {
                    const changesInArguments = changesOnField.filter(
                      (change) =>
                        getTypeOfChangeByChangeCodeAndTypeOfThing(
                          change.code,
                          'field',
                        ) === 'SOMETHING_INSIDE_THIS_CHANGED',
                    );
                    const changesRelevantToFieldAndNotArguments = _.difference(
                      changesOnField,
                      changesInArguments,
                    );

                    const fieldChangeType =
                      changesRelevantToFieldAndNotArguments[0]
                        ? getTypeOfChangeByChangeCodeAndTypeOfThing(
                            changesRelevantToFieldAndNotArguments[0].code,
                            'field',
                          )
                        : null;

                    const field =
                      type &&
                      'getFields' in type &&
                      type.getFields()[fieldName];

                    return (
                      <li key={fieldName} className="mt-3 first:mt-0">
                        <div className="flex items-center">
                          <DetailedChangeIcon
                            changeType={fieldChangeType}
                            className="mr-2"
                          />
                          <ChangeHighlighter
                            className="mr-1"
                            typeOfChange={
                              changesRelevantToFieldAndNotArguments[0]?.code ===
                                'FIELD_CHANGED_TYPE' ||
                              changesRelevantToFieldAndNotArguments[0]?.code ===
                                'FIELD_ON_INPUT_OBJECT_CHANGED_TYPE'
                                ? null
                                : fieldChangeType
                            }
                          >
                            {type && field ? (
                              <NavLink
                                className="hover:underline"
                                title="See in Schema Reference"
                                to={locationToReference({
                                  category: getSubjectSlugByGraphQLType(type),
                                  graphqlTypeName: typeName,
                                  linkedGraphqlEntity: fieldName,
                                })}
                              >
                                {fieldName}
                              </NavLink>
                            ) : (
                              fieldName
                            )}
                            {(changesRelevantToFieldAndNotArguments[0]?.code ===
                              'FIELD_CHANGED_TYPE' ||
                              changesRelevantToFieldAndNotArguments[0]?.code ===
                                'FIELD_ON_INPUT_OBJECT_CHANGED_TYPE') &&
                              ':'}
                          </ChangeHighlighter>
                          {changesRelevantToFieldAndNotArguments.length ? (
                            changesRelevantToFieldAndNotArguments.map(
                              (change) => {
                                return (
                                  <React.Fragment key={change.code}>
                                    {(change.code === 'FIELD_CHANGED_TYPE' ||
                                      change.code ===
                                        'FIELD_ON_INPUT_OBJECT_CHANGED_TYPE') && (
                                      <ChangeHighlighter
                                        className="mr-1"
                                        typeOfChange="THIS_CHANGED"
                                      >
                                        {change.childNode?.printedType &&
                                          splitPrintedTypeString(
                                            change.childNode?.printedType,
                                          ).map((segment, i) => {
                                            if (i !== 1) {
                                              // this is wrapper stuff like ([!]!)
                                              return segment;
                                            }
                                            const typeData = typeMap[segment];
                                            if (!typeData) {
                                              // can't find the type in the current schema
                                              return segment;
                                            }

                                            return (
                                              <NavLink
                                                key={segment}
                                                to={locationToReference({
                                                  category:
                                                    getSubjectSlugByGraphQLType(
                                                      typeData,
                                                    ),
                                                  graphqlTypeName: segment,
                                                })}
                                                className="hover:underline"
                                                title="See in Schema Reference"
                                              >
                                                {segment}
                                              </NavLink>
                                            );
                                          })}
                                      </ChangeHighlighter>
                                    )}
                                    <StyledChangeDescription className="mx-1">
                                      {change.code.replace(/_/g, ' ')}
                                    </StyledChangeDescription>
                                  </React.Fragment>
                                );
                              },
                            )
                          ) : (
                            <StyledChangeDescription>
                              field modified
                            </StyledChangeDescription>
                          )}
                        </div>
                        <ul
                          data-changelog-field-argument-changes
                          className="ml-6 mt-3"
                        >
                          {changesInArguments.map((change) => {
                            const argChangeType =
                              getTypeOfChangeByChangeCodeAndTypeOfThing(
                                change.code,
                                'argument',
                              );

                            return (
                              <li
                                key={JSON.stringify(change)}
                                className="mt-3 flex items-center first:mt-0"
                              >
                                <DetailedChangeIcon
                                  changeType={argChangeType}
                                  className="mr-2"
                                />
                                <ChangeHighlighter
                                  className="mr-2"
                                  typeOfChange={argChangeType}
                                >
                                  {change.argNode?.name}
                                </ChangeHighlighter>{' '}
                                <span className="text-sm lowercase text-secondary">
                                  {change.code.replace(/_/g, ' ')}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </li>
                    );
                  })}
                </ul>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

const StyledChangeDescription = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={classnames('text-sm lowercase text-secondary', className)}
    {...props}
  />
);
